import { useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';
import type { AddressType } from '@sweetgreen/garnish';

import type { Order } from '@order/graphql';
import { useNavigateToMenu } from '@order/hooks';
import { useLastValidOrder } from '@order/Order';

/**
 * Navigates to the menu from the last order.
 * If there is no last order, navigate to the locations screen instead.
 */
export const useNavigateToLastOrderMenu = () => {
  const navigation = useNavigation();
  const { lastValidOrder } = useLastValidOrder();
  const navigateToMenu = useNavigateToMenu();

  // ────────────────────────────────────────────────────────────────────

  const deliveryOrderDetail = lastValidOrder?.deliveryOrderDetail;
  const lastOrderAddressId = deliveryOrderDetail?.address?.id;
  const lastOrderRestaurantSlug = lastValidOrder?.restaurant.slug;
  const lastOrderType = orderTypeFromOrder(lastValidOrder);

  // ────────────────────────────────────────────────────────────────────

  const handleGoToLocations = useCallback(
    (locationSelectionType: AddressType) => {
      navigation.navigate('MainTabs', {
        screen: 'MenuTab',
        params: {
          screen: 'Locations',
          params: { locationSelectionType },
        },
      });
    },
    [navigation],
  );

  // ────────────────────────────────────────────────────────────────────

  return useCallback(() => {
    // Navigating to last order's delivery menu.
    if (lastOrderAddressId) {
      navigateToMenu({
        orderType: 'delivery',
        deliveryOrderDetail,
      });

      return;
    }

    // Navigating to last order's pickup/outpost menu.
    if (lastOrderRestaurantSlug) {
      navigateToMenu({
        orderType: 'pickup',
        restaurantSlug: lastOrderRestaurantSlug,
      });

      return;
    }

    // Navigating to the locations screen.
    handleGoToLocations(lastOrderType);
  }, [
    lastOrderAddressId,
    lastOrderRestaurantSlug,
    lastOrderType,
    deliveryOrderDetail,
    navigateToMenu,
    handleGoToLocations,
  ]);
};

// ────────────────────────────────────────────────────────────────────

const orderTypeFromOrder = (order?: Order): AddressType => {
  if (!order) return 'pickup';

  if (order.restaurant.isOutpost) return 'outpost';

  return order.orderType.toLowerCase() as AddressType;
};
